import React, { useState, useEffect } from "react";
import { useUser } from "contexts/v1/masterData/UserContext";
import { getRequest } from "services/api";
import UserSelector from "./UserSelector";

const toDateKey = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, "0"),
  String(date.getDate()).padStart(2, "0"),
].join("-");

const getPayrollPeriod = (month, year) => {
  const start = new Date(Number(year), Number(month) - 2, 26);
  const end = new Date(Number(year), Number(month) - 1, 25);
  return {
    startDate: toDateKey(start),
    endDate: toDateKey(end),
    dateSalary: toDateKey(new Date(Number(year), Number(month) - 1, 1)),
  };
};

const formatPeriodDate = (value) => new Date(value).toLocaleDateString("id-ID", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const CreateSalaryPage = ({ initialData, onSuccess, onError }) => {
  const { users } = useUser();

  const [formData, setFormData] = useState({
    month: initialData?.month || new Date().getMonth() + 1,
    year: initialData?.year || new Date().getFullYear(),
    users: initialData?.users || [],
    force_regenerate: true,
  });

  const [activeUserIds, setActiveUserIds] = useState(new Set());
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  useEffect(() => {
    const fetchSalarySettings = async () => {
      try {
        const response = await getRequest("/ahr/salary-settings?all=1&limit=2000");
        if (response.success && response.data) {
          const activeIds = response.data
            .filter((setting) => setting.is_active === undefined || Boolean(Number(setting.is_active)))
            .map((setting) => setting.user_id);
          setActiveUserIds(new Set(activeIds));
        }
      } catch (error) {
        console.error("Failed to fetch salary settings", error);
      } finally {
        setIsLoadingSettings(false);
      }
    };
    fetchSalarySettings();
  }, []);

  const activeUsers = (users.data || []).filter((user) => activeUserIds.has(user.id));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === "month" || name === "year" ? parseInt(value, 10) : e.target.type === "checkbox" ? e.target.checked : value
    }));
  };

  const handleUserChange = (selectedUserIds) => {
    setFormData(prev => ({
      ...prev,
      users: selectedUserIds
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.month || !formData.year || formData.users.length === 0) {
      onError(new Error("Please select at least one user and fill in all required fields"));
      return;
    }

    // Call onSuccess with the form data
    const payrollPeriod = getPayrollPeriod(formData.month, formData.year);
    onSuccess({
      ...formData,
      start_date: payrollPeriod.startDate,
      end_date: payrollPeriod.endDate,
      date_salary: payrollPeriod.dateSalary,
      force_regenerate: formData.force_regenerate,
      overwrite_existing: formData.force_regenerate,
    });
  };

  const payrollPeriod = getPayrollPeriod(formData.month, formData.year);

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Generate Salary</h3>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">
                  Month <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="month"
                  value={formData.month}
                  onChange={handleInputChange}
                  required
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>
                      {new Date(new Date().getFullYear(), month - 1, 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">
                  Year <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  min={new Date().getFullYear() - 5}
                  max={new Date().getFullYear() + 1}
                  required
                />
              </div>
            </div>
          </div>

          <div className="alert alert-info py-2">
            <div>
              Periode payroll: <strong>{formatPeriodDate(payrollPeriod.startDate)} - {formatPeriodDate(payrollPeriod.endDate)}</strong>
            </div>
            <div className="small mt-1">
              Periode ini dikirim ke API sebagai <code>start_date</code> dan <code>end_date</code>. Perhitungan Uang Makan Pagi dan Over DO mengikuti periode ini.
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Start Date</label>
                <input type="date" className="form-control" value={payrollPeriod.startDate} readOnly />
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">End Date</label>
                <input type="date" className="form-control" value={payrollPeriod.endDate} readOnly />
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Date Salary</label>
                <input type="date" className="form-control" value={payrollPeriod.dateSalary} readOnly />
              </div>
            </div>
          </div>

          <div className="alert alert-warning py-2">
            <strong>Catatan:</strong> Over DO dan Uang Makan Pagi dihitung oleh API. FE hanya menampilkan hasil final dan tidak menghitung ulang nominal salary.
          </div>

          <label className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              name="force_regenerate"
              checked={formData.force_regenerate}
              onChange={handleInputChange}
            />
            <span className="form-check-label">
              Regenerate/overwrite salary jika periode yang sama sudah ada
            </span>
          </label>

          <div className="mb-3">
            <label className="form-label">
              Select Users <span className="text-danger">*</span>
            </label>
            <UserSelector
              value={formData.users}
              onChange={handleUserChange}
              users={activeUsers}
              showSelectAll
            />
            <div className="form-text">
              Selected users: {formData.users.length} (Hanya menampilkan karyawan yang disetting "Active" di Salary Settings)
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button type="submit" className="btn btn-primary" disabled={users.loading || isLoadingSettings}>
              {users.loading || isLoadingSettings ? "Loading data..." : "Generate Salary"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSalaryPage;
